import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const HdfcBusinessLoanEmiCal= () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "What is this EMI calculator for?",
        "answer": "It helps entrepreneurs estimate monthly repayment obligations on HDFC business loans, allowing for better financial planning."
      },
      {
        "question": "Are floating interest rates supported?",
        "answer": "Yes—you can simulate both fixed and floating rates. Use the tool to assess EMI impact under future rate scenarios."
      },
      {
        "question": "Can prepayment be accounted for?",
        "answer": "Yes. Adjust the principal balance after prepayment in the calculator to see how EMI or tenure gets affected."
      },
      {
        "question": "How long can the repayment tenure be?",
        "answer": "HDFC Bank business loans typically allow up to 7 years or more. You can model different durations easily."
      },
      {
        "question": "Is collateral obligatory?",
        "answer": "Collateral requirements depend on loan type and size. Your EMI estimate remains accurate regardless of collateral status."
      },
      {
        "question": "Do EMIs include processing fees?",
        "answer": "No—they consist of principal and interest only, so budget separately for processing or administrative costs."
      },
      {
        "question": "Can EMI change mid-tenure?",
        "answer": "For fixed-rate loans, EMI remains unchanged. Floating-rate repayments may vary when HDFC revises rates."
      },
      {
        "question": "Can I model prepayment penalties?",
        "answer": "Enter the reduced principal post-prepayment and recalculate – penalty details are handled by bank terms."
      },
      {
        "question": "Is the amortisation schedule exportable?",
        "answer": "Yes, you can download or copy it for bookkeeping, reporting, or strategic planning."
      },
      {
        "question": "How accurate are the EMI estimates?",
        "answer": "They are generated using standard formulas and are very reliable. Minor variance may occur due to day count or rounding."
      },
      {
        "question": "Is the tool mobile-compatible?",
        "answer": "Absolutely—it works seamlessly on smartphones, tablets, and laptops without compromising functionality."
      },
      {
        "question": "Can multiple co-applicants use it?",
        "answer": "Absolutely—EMI output doesn’t change based on borrower setup, helping partners coordinate repayment contributions."
      },
      {
        "question": "Are EMIs adjustable to business cycles?",
        "answer": "Yes—you can simulate flexible repayment cycles, tenure choices, and payment holidays if applicable under sanctioned terms."
      },
      {
        "question": "How do I plan for interest rate fluctuations?",
        "answer": "Use the tool to run simulations at varying interest values—use this to budget for possible rate upticks."
      },
      {
        "question": "Is my data stored securely?",
        "answer": "No personal or financial information is retained. The tool ensures complete privacy and protection while assisting business planning."
      }
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>HDFC Business Loan EMI Calculator - Easy & Accurate Estimation</title>
          <meta  name='description' content='Use our HDFC business loan EMI Calculator to calculate your monthly installments easily. Make informed decisions with precise EMI estimates.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/hdfc-business-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> HDFC Business Loan EMI Calculator</span>
          </li>
        </ol>
      </nav> */}
        <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            HDFC Business Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Plan Your HDFC Business Loan with TheFinMaster’s EMI Calculator
      </h2>
      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
        Growing a business—be it setting up a new venture, expanding operations, upgrading equipment, or maintaining working capital—often requires financial support. HDFC Bank Business Loans offer flexible funding solutions with attractive interest rates, customized tenures, and quick approvals. But effective growth requires a solid repayment plan, and that’s where TheFinMaster’s HDFC Business Loan EMI Calculator becomes indispensable. It helps you project EMIs accurately, evaluate multiple repayment scenarios, and align your financial strategy with real-time business needs.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         What is a Business Loan EMI?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        An Equated Monthly Installment (EMI) on a business loan is the fixed amount you pay monthly, covering both the principal and interest components. HDFC Bank’s business loans can be tailored with flexible tenures, fixed or floating interest rates, and borrower-friendly prepayment options. Structuring repayment through EMIs helps businesses stabilize cash flow, support financial planning, and maintain profitability, even during growth phases.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         What is the HDFC Business Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The HDFC Business Loan EMI Calculator by TheFinMaster is a dynamic and intuitive online tool that calculates your EMI based on:
        <ul className="list-disc pl-6 mt-2">
          <li>Loan Amount – Total amount to be borrowed</li>
          <li>Interest Rate – HDFC’s prevailing interest rate for business loans</li>
          <li>Tenure – Loan repayment period (in months or years)</li>
          <li>Rate Type – Fixed or floating interest inputs</li>
        </ul>
        Key Capabilities:
        <ul className="list-disc pl-6 mt-2">
          <li>Instant display of EMI, total interest, and cumulative repayment</li>
          <li>Access to a month-by-month amortisation schedule</li>
          <li>Multi-scenario testing for rates and tenures</li>
          <li>100% secure and anonymous—no login or data tracking</li>
          <li>Fully responsive across devices</li>
        </ul>
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Why Use TheFinMaster’s HDFC EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Clear EMI Estimates:</strong> Instantly see how much your business will need to allocate monthly.</li>
          <li><strong>Scenario Testing:</strong> Compare different tenures, rates, and repayment cycles based on projected income.</li>
          <li><strong>Detailed Amortisation:</strong> Understand principal reduction over time and interest paid year-wise.</li>
          <li><strong>Strategic Budget Alignment:</strong> Integrate EMI obligations into your business cash flow forecast.</li>
          <li><strong>Secure & Private:</strong> No personal data required—use with confidence.</li>
          <li><strong>Downloadable Schedules:</strong> Export for financial planning, bookkeeping, or stakeholder presentations.</li>
        </ul>
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         How the EMI Calculator Works
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The EMI formula used is:
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI = [P × r × (1 + r)^N] / [(1 + r)^N – 1]
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Where:
        <ul className="list-disc pl-6 mt-2">
          <li>P = Principal amount of the loan</li>
          <li>r = Monthly interest rate (annual rate ÷ 12 ÷ 100)</li>
          <li>N = Repayment tenure in months</li>
        </ul>
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
         <strong>Example:</strong><br />
        Loan Amount: ₹75,00,000<br />
        Interest Rate: 10% p.a. → r ≈ 0.833%<br />
        Tenure: 6 years (72 months)<br />
        Estimated EMI = ₹1,26,204<br />
        Total Repayment = ₹90,87,888<br />
        Total Interest Paid = ₹15,87,888<br />
        This helps you decide if your revenue streams can support such monthly liabilities.
      </p>
    </div>
    <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Sample Amortisation Table (First 12 Months)
      </h2>
      <div className="mt-6">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">EMI (₹)</th>
                <th className="border px-4 py-2 text-left">Interest (₹)</th>
                <th className="border px-4 py-2 text-left">Principal (₹)</th>
                <th className="border px-4 py-2 text-left">Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "1,26,204", "62,500", "63,704", "74,36,296"],
                ["2", "1,26,204", "61,967", "64,237", "73,72,059"],
                ["3", "1,26,204", "61,433", "64,771", "73,07,287"],
                ["4", "1,26,204", "60,893", "65,311", "72,41,976"],
                ["5", "1,26,204", "60,350", "65,854", "71,76,122"],
                ["6", "1,26,204", "59,800", "66,404", "71,09,718"],
                ["7", "1,26,204", "59,245", "66,959", "70,42,759"],
                ["8", "1,26,204", "58,684", "67,520", "69,74,239"],
                ["9", "1,26,204", "58,029", "68,175", "69,06,064"],
                ["10", "1,26,204", "57,190", "69,014", "68,37,050"],
                ["11", "1,26,204", "56,464", "69,740", "67,67,310"],
                ["12", "1,26,204", "55,564", "70,640", "66,96,670"]
              ].map(([month, emi, interest, principal, balance], index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{month}</td>
                  <td className="border px-4 py-2">{emi}</td>
                  <td className="border px-4 py-2">{interest}</td>
                  <td className="border px-4 py-2">{principal}</td>
                  <td className="border px-4 py-2">{balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4 mt-4">
          {[
            ["1", "1,26,204", "62,500", "63,704", "74,36,296"],
            ["2", "1,26,204", "61,967", "64,237", "73,72,059"],
            ["3", "1,26,204", "61,433", "64,771", "73,07,287"],
            ["4", "1,26,204", "60,893", "65,311", "72,41,976"],
            ["5", "1,26,204", "60,350", "65,854", "71,76,122"],
            ["6", "1,26,204", "59,800", "66,404", "71,09,718"],
            ["7", "1,26,204", "59,245", "66,959", "70,42,759"],
            ["8", "1,26,204", "58,684", "67,520", "69,74,239"],
            ["9", "1,26,204", "58,029", "68,175", "69,06,064"],
            ["10", "1,26,204", "57,190", "69,014", "68,37,050"],
            ["11", "1,26,204", "56,464", "69,740", "67,67,310"],
            ["12", "1,26,204", "55,564", "70,640", "66,96,670"]
          ].map(([month, emi, interest, principal, balance], index) => (
            <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
              <div><span className="font-semibold">Month:</span> {month}</div>
              <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
              <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
              <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
              <div><span className="font-semibold">Balance (₹):</span> {balance}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Factors Affecting Your EMI
      </h2>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount</strong>
         Larger principal results in higher EMI and interest cost. Borrow only what your business needs and can service.</li>
        <li><strong>Interest Rate</strong>
         Even minimal rate fluctuations affect your repayment significantly. Monitor and recalculate EMI with changes.</li>
        <li><strong>Tenure Selection</strong>
         Longer tenures reduce monthly outflow but raise aggregate interest; shorter tenure saves money but increases monthly obligation.</li>
        <li><strong>Rate Type</strong>
         Fixed interest offers stability; floating rates may fluctuate, impacting your EMI loads and planning process.</li>
        <li><strong>Prepayment Options</strong>
         HDFC permits prepayments with nominal charges—calculate the EMI impact post-partial repayment to reduce costs or tenure.</li>
        <li><strong>Business Cash Flow</strong>
         Align EMI cycles with monthly revenue patterns to prevent payment stress and maintain healthy liquidity.</li>
        <li><strong>Collateral or Security Requirement</strong>
         Depending on loan size and type, collateral obligations may apply; though EMI stays unchanged, understanding terms helps planning.</li>
      </ul>
    </div>
  </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
                     {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HdfcBusinessLoanEmiCal;
